import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { v4 as uuidv4 } from 'uuid';
import {
  AiDocumentationFieldEnum,
  AiDocumentationFieldsGeneratedDocument,
  AiDocumentationVersionFieldsGeneratedDocument,
  AttachmentFileInputObject,
  useAiGenerateDocumentationFieldsMutation,
  useAiGenerateDocumentationVersionFieldsMutation,
} from 'graphql-common';
import { logger } from '@lib/services/logger';
import { useSharedAiBudget } from 'components/AiAssistant/AiBudgetContext';

// The generation result arrives ONLY on the subscription — there is no persisted
// transcript to reconcile against (unlike the chat assistant). If the result
// frame is missed or never published, the field would hang in `generating`
// forever, so a bounded timeout fails it with a synthetic error code.
const GENERATION_TIMEOUT_MS = 90_000;
export const AI_GENERATION_TIMEOUT_ERROR_CODE = 'generation_timeout';

// A documentation VERSION's own fields (currently only NOTES) generate through
// a separate mutation/subscription pair from the document-level fields
// (NAME/DESCRIPTION/CODE/TAGS/CATEGORY) — BE guide §7a.
const VERSION_FIELDS = new Set<AiDocumentationFieldEnum>([
  AiDocumentationFieldEnum.Notes,
]);
const isVersionField = (field: AiDocumentationFieldEnum) =>
  VERSION_FIELDS.has(field);

// `notFound` is the BE's explicit "model ran but had nothing to fill" outcome
// (e.g. an image with no readable content) — render it neutrally, not as an
// error; Regenerate won't help unless the file changes (BE guide §7).
export type AiFieldGenerationStatus =
  'idle' | 'generating' | 'done' | 'notFound' | 'error';

export type AiFieldGenerationState = {
  status: AiFieldGenerationStatus;
  // Scalar fields (NAME/DESCRIPTION/CODE/NOTES): the generated value on
  // success; '' when the model had nothing to fill, null otherwise.
  text: string | null;
  // List field (TAGS): the suggested values on success (possibly empty), null
  // otherwise. CATEGORY: the suggested category id, null otherwise. Exactly
  // one of text/values/categoryId is populated per the field kind.
  values: string[] | null;
  categoryId: string | null;
  // Machine-readable failure reason; map to copy in the UI layer.
  errorCode: string | null;
  generationId: string | null;
};

// Exactly one source must be provided (mirrors the BE contract: a saved version
// to regenerate from, or a freshly uploaded file for create / a new version).
export type AiFieldGenerationSource =
  | { documentationVersionId: string; file?: never }
  | { file: AttachmentFileInputObject; documentationVersionId?: never };

export type GenerateFieldParams = {
  field: AiDocumentationFieldEnum;
  source: AiFieldGenerationSource;
  // Free-text hint applied only when generating CODE (ignored for other
  // fields, and not accepted at all by the version-fields mutation).
  codePrompt?: string;
};

export type GenerateAllParams = {
  fields: AiDocumentationFieldEnum[];
  source: AiFieldGenerationSource;
  codePrompt?: string;
};

type FieldStates = Partial<
  Record<AiDocumentationFieldEnum, AiFieldGenerationState>
>;

type ActiveGeneration = {
  fields: AiDocumentationFieldEnum[];
  unsubscribe: () => void;
  timer: ReturnType<typeof setTimeout>;
};

// Shared shape of both aiDocumentationFieldsGenerated and
// aiDocumentationVersionFieldsGenerated frames (BE guide §7/§7a) — identical
// selection set, just delivered over two different subscriptions depending on
// whether the requested field lives on the document or the version.
type AiFieldsGenerationFrame = {
  generationId: string;
  errorCode?: string | null;
  results: Array<{
    field: AiDocumentationFieldEnum;
    text?: string | null;
    values?: string[] | null;
    categoryId?: string | null;
    notFound: boolean;
    errorCode?: string | null;
  }>;
};

const IDLE_STATE: AiFieldGenerationState = {
  status: 'idle',
  text: null,
  values: null,
  categoryId: null,
  errorCode: null,
  generationId: null,
};

// A field's terminal state, minus the generationId that `settleFields` stamps.
type SettledFieldState = Pick<
  AiFieldGenerationState,
  'status' | 'text' | 'values' | 'categoryId' | 'errorCode'
>;

const NOT_FOUND_STATE: SettledFieldState = {
  status: 'notFound',
  text: null,
  values: null,
  categoryId: null,
  errorCode: null,
};

const errorState = (errorCode: string | null = null): SettledFieldState => ({
  status: 'error',
  text: null,
  values: null,
  categoryId: null,
  errorCode,
});

/**
 * Drives AI auto-fill for documentation fields (NAME / DESCRIPTION / CODE /
 * TAGS / CATEGORY) and version fields (NOTES).
 *
 * UI-agnostic: it owns the mutation + per-generation subscription and exposes
 * per-field status only. Applying the generated `text` into the form, rendering
 * loaders/errors, and gating by plan are the consumer's responsibility.
 *
 * Concurrency: each field tracks its own `generationId`, so multiple fields can
 * generate at once from a single hook instance. Re-triggering a field cancels
 * its previous in-flight generation first.
 */
export default function useAiFieldGeneration() {
  const client = useApolloClient();
  // Re-read the credits whenever a generation settles. Owned here rather than
  // by each caller: this is the one place every outcome funnels through, so no
  // surface can forget it and none of them has to thread a callback down to
  // the field triggers. Null outside AiBudgetProvider (tests) — nothing to
  // refresh then.
  const aiBudget = useSharedAiBudget();
  const refetchAiBudget = aiBudget?.refetch;
  const [generateFieldsMutation] = useAiGenerateDocumentationFieldsMutation();
  const [generateVersionFieldsMutation] =
    useAiGenerateDocumentationVersionFieldsMutation();
  const [stateByField, setStateByField] = useState<FieldStates>({});

  // generationId -> active generation, so a late/duplicate frame for a
  // superseded generation can be ignored and cleaned up deterministically.
  const activeRef = useRef<Record<string, ActiveGeneration>>({});

  const teardown = useCallback((generationId: string) => {
    const active = activeRef.current[generationId];
    if (!active) return;
    clearTimeout(active.timer);
    try {
      active.unsubscribe();
    } catch (error) {
      logger.error('AiFieldGeneration: unsubscribe failed', {
        generationId,
        error,
      });
    }
    delete activeRef.current[generationId];
  }, []);

  // Settle a generation's still-pending fields and tear it down. `resolve`
  // returns each field's terminal state, so the failure paths hand back one
  // shared outcome while the result frame fans out per entry.
  const settleFields = useCallback(
    (
      generationId: string,
      resolve: (field: AiDocumentationFieldEnum) => SettledFieldState,
    ) => {
      const active = activeRef.current[generationId];
      // Already torn down — a result was delivered (or the fields were
      // cancelled), so a late `complete`/timeout must not clobber it.
      if (!active) return;
      const { fields } = active;
      teardown(generationId);
      setStateByField((prev) => {
        const next = { ...prev };
        fields.forEach((field) => {
          // Only settle a field this generation still owns — a stale frame from
          // a superseded generation must not clobber newer state.
          if (prev[field]?.generationId !== generationId) return;
          next[field] = { ...resolve(field), generationId };
        });
        return next;
      });
      // Every terminal outcome passes through here — a result, a per-field
      // error, a subscription failure, the 90s timeout — and a failed
      // generation can still have been metered, so the credits are re-read on
      // all of them, not just success. Once per generation, so a five-field
      // batch autofill re-reads once rather than five times.
      refetchAiBudget?.();
    },
    [refetchAiBudget, teardown],
  );

  // Release `field` from whatever generation currently owns it. A combined
  // generation covers several fields, so only the last one leaving tears the
  // subscription down — tearing it down for a single field would strand its
  // siblings in `generating` with no result frame and no timeout to fail them.
  const cancelActiveForField = useCallback(
    (field: AiDocumentationFieldEnum) => {
      Object.entries(activeRef.current).forEach(([generationId, active]) => {
        if (!active.fields.includes(field)) return;
        const remaining = active.fields.filter((item) => item !== field);
        if (!remaining.length) {
          teardown(generationId);
          return;
        }
        activeRef.current[generationId] = { ...active, fields: remaining };
      });
    },
    [teardown],
  );

  const run = useCallback(
    ({ fields, source, codePrompt }: GenerateAllParams) => {
      if (!fields.length) return '';
      const generationId = uuidv4();
      const trimmedCodePrompt = codePrompt?.trim() || undefined;
      const isVersion = isVersionField(fields[0]);

      if (fields.some((field) => isVersionField(field) !== isVersion)) {
        logger.error('AiFieldGeneration: mixed document/version fields', {
          generationId,
          fields,
        });
        // Programmer error: no generation was registered, so return the empty
        // sentinel (like the empty-fields guard above) rather than a phantom id
        // a caller could await forever.
        return '';
      }

      fields.forEach(cancelActiveForField);

      setStateByField((prev) => ({
        ...prev,
        ...fields.reduce<FieldStates>(
          (acc, field) => ({
            ...acc,
            [field]: {
              status: 'generating',
              text: null,
              values: null,
              categoryId: null,
              errorCode: null,
              generationId,
            },
          }),
          {},
        ),
      }));

      // The result arrives on this subscription only, so it must be registered
      // server-side BEFORE the mutation publishes — otherwise the one-off result
      // frame is published into the gap before registration, never delivered,
      // and the field hangs until the 90s timeout. Subscribing here is not enough:
      // `.subscribe()` returning does not mean the ActionCable channel is live.
      // graphql-ruby confirms registration by delivering an empty frame as the
      // first `next`; fire the mutation only then (see `sendMutation` below).
      // Frames carry no `id` and are read only here, so skip the cache.
      const observable = client.subscribe<{ data: AiFieldsGenerationFrame }>({
        query: isVersion
          ? AiDocumentationVersionFieldsGeneratedDocument
          : AiDocumentationFieldsGeneratedDocument,
        variables: { generationId },
        fetchPolicy: 'no-cache',
      });

      // Fire the generation mutation exactly once, only after the subscription is
      // confirmed live (first frame received). The version-fields input has no
      // codePrompt field at all (notes takes no hint, BE guide §7a) — build each
      // mutation's attributes separately rather than sending an extra key the
      // schema doesn't accept.
      let mutationSent = false;
      const sendMutation = () => {
        if (mutationSent) return;
        mutationSent = true;
        const enqueue = isVersion
          ? generateVersionFieldsMutation({
              variables: {
                attributes: {
                  fields,
                  generationId,
                  ...source,
                },
              },
            })
          : generateFieldsMutation({
              variables: {
                attributes: {
                  fields,
                  generationId,
                  codePrompt: trimmedCodePrompt,
                  ...source,
                },
              },
            });

        enqueue.catch((error) => {
          // A synchronous failure (e.g. validation / plan-gating) never reaches
          // the subscription, so surface it here.
          logger.error('AiFieldGeneration: enqueue mutation failed', {
            generationId,
            fields,
            error,
          });
          settleFields(generationId, () => errorState());
        });
      };

      const subscription = observable.subscribe({
        next: (frame) => {
          // The first frame is graphql-ruby's empty registration frame, proving
          // the ActionCable channel is live server-side — the mutation is safe to
          // publish into now. (Guarded to fire once; later frames are no-ops.)
          sendMutation();
          // graphql-ruby delivers subscription-level GraphQL errors HERE as
          // `{ data: null, errors: [...] }` (NOT via the `error` callback, and
          // not as Apollo's own unified `SubscribeResult.error` — this is a
          // raw graphql-ruby frame field Apollo's types don't model), so they
          // must be handled before the data path or they vanish silently and
          // surface only as the 90s timeout.
          const frameErrors =
            (frame as { errors?: readonly { message: string }[] })?.errors ??
            [];
          if (frameErrors.length) {
            logger.error('AiFieldGeneration: subscription returned errors', {
              generationId,
              fields,
              errors: frameErrors,
            });
            settleFields(generationId, () => errorState());
            return;
          }
          // The initial graphql-ruby registration frame carries a null/empty
          // payload; ignore anything without a matching, populated result.
          const result = frame?.data?.data;
          if (!result || result.generationId !== generationId) return;
          if (!result.errorCode && !result.results?.length) return;

          // A pre-generation failure (gate/media/vanished record) stops the
          // whole request before any field could be generated — `results` is
          // empty (BE guide §7).
          if (result.errorCode) {
            logger.debug('AiFieldGeneration: request-level failure', {
              generationId,
              fields,
              errorCode: result.errorCode,
            });
            settleFields(generationId, () => errorState(result.errorCode));
            return;
          }

          logger.debug('AiFieldGeneration: result frame received', {
            generationId,
            fields,
            resultFields: result.results.map((entry) => entry.field),
          });

          const resultsByField = new Map(
            result.results.map((entry) => [entry.field, entry]),
          );

          settleFields(generationId, (field) => {
            const fieldResult = resultsByField.get(field);
            // A requested field missing from the frame shouldn't happen; settle
            // it neutrally rather than let it hang until the timeout.
            if (!fieldResult) return NOT_FOUND_STATE;

            if (fieldResult.errorCode) return errorState(fieldResult.errorCode);

            if (
              fieldResult.notFound ||
              (fieldResult.text == null &&
                fieldResult.values == null &&
                fieldResult.categoryId == null)
            ) {
              return NOT_FOUND_STATE;
            }

            return {
              status: 'done',
              text: fieldResult.text ?? null,
              values: fieldResult.values ?? null,
              categoryId: fieldResult.categoryId ?? null,
              errorCode: null,
            };
          });
        },
        error: (error) => {
          logger.error('AiFieldGeneration: subscription error', {
            generationId,
            fields,
            error,
          });
          settleFields(generationId, () => errorState());
        },
        complete: () => {
          // The ActionCable channel closed (graphql-ruby `more: false`). After a
          // delivered result this is a no-op (the generation is already torn
          // down); if it arrives while still generating, the channel closed
          // without ever delivering a result — fail now instead of hanging
          // until the timeout.
          logger.debug('AiFieldGeneration: subscription completed', {
            generationId,
            fields,
          });
          settleFields(generationId, () => errorState());
        },
      });

      const timer = setTimeout(() => {
        logger.error('AiFieldGeneration: generation timed out', {
          generationId,
          fields,
        });
        settleFields(generationId, () =>
          errorState(AI_GENERATION_TIMEOUT_ERROR_CODE),
        );
      }, GENERATION_TIMEOUT_MS);

      // Copy `fields`: cancelling one field rewrites this list, and the
      // mutation variables / log lines must keep the originally requested set.
      activeRef.current[generationId] = {
        fields: [...fields],
        unsubscribe: () => subscription.unsubscribe(),
        timer,
      };
      return generationId;
    },
    [
      cancelActiveForField,
      client,
      generateFieldsMutation,
      generateVersionFieldsMutation,
      settleFields,
    ],
  );

  const generate = useCallback(
    ({ field, source, codePrompt }: GenerateFieldParams) =>
      run({ fields: [field], source, codePrompt }),
    [run],
  );

  const generateAll = useCallback(
    ({ fields, source, codePrompt }: GenerateAllParams) =>
      run({ fields, source, codePrompt }),
    [run],
  );

  // Stop waiting on a field (client-side only — the BE keeps generating). Resets
  // the field back to idle.
  const cancel = useCallback(
    (field: AiDocumentationFieldEnum) => {
      cancelActiveForField(field);
      setStateByField((prev) => ({ ...prev, [field]: { ...IDLE_STATE } }));
    },
    [cancelActiveForField],
  );

  // Clear a field's result after the consumer has applied (or dismissed) it.
  const reset = useCallback((field: AiDocumentationFieldEnum) => {
    setStateByField((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const getFieldState = useCallback(
    (field: AiDocumentationFieldEnum): AiFieldGenerationState =>
      stateByField[field] ?? IDLE_STATE,
    [stateByField],
  );

  // Tear down every in-flight subscription/timer on unmount.
  useEffect(
    () => () => {
      Object.keys(activeRef.current).forEach((generationId) => {
        const active = activeRef.current[generationId];
        clearTimeout(active.timer);
        try {
          active.unsubscribe();
        } catch {
          // ignore teardown errors on unmount
        }
      });
      activeRef.current = {};
    },
    [],
  );

  return { stateByField, getFieldState, generate, generateAll, cancel, reset };
}
